import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth, postAuthDestination } from "@/contexts/AuthContext";

/**
 * Google Identity Services One Tap prompt. Shows automatically when a Google
 * session exists on the device. Verifies the credential server-side and signs
 * the user in.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleOneTap({ disabled = false }) {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const initialized = useRef(false);

  useEffect(() => {
    if (disabled || user) return;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    const inject = () => {
      if (window.google?.accounts?.id && !initialized.current) {
        initialized.current = true;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (resp) => {
            try {
              const r = await api.post("/auth/google/onetap", {
                credential: resp.credential,
                role: sessionStorage.getItem("kb_onboarding_role") || "client",
              });
              if (r.data.session_token) localStorage.setItem("kb_session_token", r.data.session_token);
              setUser(r.data.user);
              navigate(postAuthDestination(r.data.user));
            } catch (e) {
              console.error("One Tap failed", e);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: false,
          use_fedcm_for_prompt: true,
        });
        window.google.accounts.id.prompt();
      }
    };

    if (window.google?.accounts?.id) {
      inject();
    } else {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.defer = true;
      s.onload = inject;
      document.body.appendChild(s);
    }
  }, [disabled, user, navigate, setUser]);

  return null;
}
